export const PROJECT_CANVAS_ASSET_KINDS = [
	"text",
	"lyrics",
	"music",
	"tts",
	"image",
	"video",
	"asset_3d",
] as const;

export type ProjectCanvasAssetKind =
	(typeof PROJECT_CANVAS_ASSET_KINDS)[number];

export interface ProjectCanvasConnectionPolicyRow {
	readonly source: ProjectCanvasAssetKind;
	readonly targets: Readonly<Record<ProjectCanvasAssetKind, boolean>>;
}

export interface ProjectCanvasConnectionPolicyResult {
	readonly assetKinds: readonly ProjectCanvasAssetKind[];
	readonly rows: readonly ProjectCanvasConnectionPolicyRow[];
}

// Product-authored directional rules. This table is intentionally independent
// from the mirrored contract golden so the TypeScript diagnostic candidate can
// expose a real implementation disagreement instead of replaying expectations.
const CONNECTIONS = {
	text: {
		text: true,
		lyrics: true,
		music: true,
		tts: true,
		image: true,
		video: true,
		asset_3d: true,
	},
	lyrics: {
		text: true,
		lyrics: false,
		music: true,
		tts: true,
		image: true,
		video: true,
		asset_3d: false,
	},
	music: {
		text: true,
		lyrics: true,
		music: true,
		tts: false,
		image: true,
		video: true,
		asset_3d: false,
	},
	tts: {
		text: true,
		lyrics: false,
		music: false,
		tts: true,
		image: false,
		video: true,
		asset_3d: false,
	},
	image: {
		text: true,
		lyrics: false,
		music: false,
		tts: false,
		image: true,
		video: true,
		asset_3d: true,
	},
	video: {
		text: true,
		lyrics: false,
		music: true,
		tts: true,
		image: true,
		video: true,
		asset_3d: true,
	},
	asset_3d: {
		text: true,
		lyrics: false,
		music: false,
		tts: false,
		image: true,
		video: true,
		asset_3d: true,
	},
} as const satisfies Readonly<
	Record<
		ProjectCanvasAssetKind,
		Readonly<Record<ProjectCanvasAssetKind, boolean>>
	>
>;

export function canConnectProjectCanvasAssets(
	source: ProjectCanvasAssetKind,
	target: ProjectCanvasAssetKind,
): boolean {
	return CONNECTIONS[source][target];
}

export function projectCanvasConnectionPolicy(): ProjectCanvasConnectionPolicyResult {
	return {
		assetKinds: [...PROJECT_CANVAS_ASSET_KINDS],
		rows: PROJECT_CANVAS_ASSET_KINDS.map((source) => ({
			source,
			targets: Object.fromEntries(
				PROJECT_CANVAS_ASSET_KINDS.map((target) => [
					target,
					canConnectProjectCanvasAssets(source, target),
				]),
			) as Record<ProjectCanvasAssetKind, boolean>,
		})),
	};
}
